import { useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import Api from '../Network';
import { UseTwilioWhatsAppReturn } from '../types/types';

const useTwilioWhatsApp = (): UseTwilioWhatsAppReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  // Send verification code
  const sendVerificationCode = async (phoneNumber: string): Promise<string | null> => {
    setLoading(true);
    setError(null);

    try {
      // Call backend API to send verification code
      const response: any = await Api.post('/api/password/otp/send', {
        phone: phoneNumber,
        role: 'admin',
      });

      // Check for success - handle both 'success' and 'status' fields
      if (response && (response.success === true || response.status === true)) {
        message.success(t('auth.resetPasswordSent'));

        // Display the 6-digit OTP code for testing
        if (response.otp) {
          message.info(`Your verification code: ${response.otp}`, 10);
        }

        return response.otp || 'sent';
      } else {
        // Handle error response - check both 'message' and 'msg' fields
        const errorMsg = response?.message || response?.msg || t('auth.resetPasswordFailed');
        message.error(errorMsg);
        return null;
      }
    } catch (err: any) {
      // Log the full error for debugging
      console.error('OTP Send Error:', err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        err?.message ||
        'Unknown error occurred';
      setError(errorMessage);
      message.error(errorMessage || t('auth.resetPasswordFailed'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const resendVerificationCode = async (phoneNumber: string): Promise<string | null> => {
    setLoading(true);
    setError(null);

    try {
      // Call backend API to resend verification code (should call send endpoint, not verify)
      const response: any = await Api.post('/api/password/otp/send', {
        phone: phoneNumber,
        role: 'admin',
      });

      // Debug: log the response to see what we're getting
      console.log('OTP Resend Response:', response);

      // Check for success - handle both 'success' and 'status' fields
      if (response && (response.success === true || response.status === true)) {
        message.success(t('auth.codeResent'));

        // Display the 6-digit OTP code for testing
        if (response.otp) {
          message.info(`Your verification code: ${response.otp}`, 10);
        }

        return response.otp || 'sent';
      } else {
        // Handle error response - check both 'message' and 'msg' fields
        const errorMsg = response?.message || response?.msg || t('auth.resendFailed');
        message.error(errorMsg);
        return null;
      }
    } catch (err: any) {
      // Log the full error for debugging
      console.error('OTP Resend Error:', err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        err?.message ||
        'Unknown error occurred';
      setError(errorMessage);
      message.error(errorMessage || t('auth.resendFailed'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    sendVerificationCode,
    resendVerificationCode,
    loading,
    error,
  };
};

export default useTwilioWhatsApp;
